import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Table,
  Select,
  Button,
  Tag,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Spin,
  Tooltip,
} from "antd";
import {
  UploadOutlined,
  PaperClipOutlined,
  InfoCircleOutlined,
  InfoCircleFilled,
} from "@ant-design/icons";
import TicketUpdateModal from "./TicketUpdateModal";
import { CALL_API } from "common/API";
import { CSVLink } from "react-csv";
import { STATUS_CODE } from "common/Constants";
import { useSelector } from "react-redux";
import {
  getAssetsList,
  getMenuList,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import download from "downloadjs";
import ViewDocument from "reusableComponent/Popup/viewDocument";

const { Option } = Select;
const { TextArea } = Input;

/* ─── Styled ─────────────────────────────────────────────── */
const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  // margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CharCount = styled.div`
  font-size: 11px;
  color: #aaa;
  text-align: right;
  margin-top: 2px;
`;

const OwnerLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #1a1a1a;
  margin-bottom: 8px;

  .optional {
    color: #aaa;
    font-weight: 400;
  }
`;

const statusColors = {
  Pending: { bg: "#dbeafe", color: "#1d4ed8" },
  "In Progress": { bg: "#fef9c3", color: "#a16207" },
  Rejected: { bg: "#fee2e2", color: "#991b1b" },
  Resolved: { bg: "#dcfce7", color: "#15803d" },
};

const StatusTag = styled(Tag)`
  border-radius: 12px;
  padding: 2px 10px;
  background: ${({ $status }) => statusColors[$status]?.bg || "#f0f0f0"};
  color: ${({ $status }) => statusColors[$status]?.color || "#555"};
  border: none;
  font-size: 12px;
`;

const AttachLink = styled.a`
  color: #2563eb;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    text-decoration: underline;
  }
`;

/* ─── Constants ──────────────────────────────────────────── */
const UPDATABLE_STATUSES = ["Pending", "In Progress"];
const priorities = ["High", "Medium", "Low", "Urgent"];
const statuses = ["Open", "Pending", "In Progress", "Rejected", "Resolved"];

/* ─── Component ──────────────────────────────────────────── */
const TicketsAndQueries = ({ exportRef }) => {
  const assetList = useSelector(getAssetsList);
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);

  const Menulist =
    menuList[userRoleId] || Object.values(menuList)[0] || menuList;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewURL, setPreviewURL] = useState();

  // Filter form — same pattern as HRPerformance
  const [filterForm] = Form.useForm();
  const assetFilter = Form.useWatch("assetFilter", filterForm) || [];
  const priorityFilter = Form.useWatch("priorityFilter", filterForm) || [];
  const statusFilter = Form.useWatch("statusFilter", filterForm) || [];

  // Send to Service modal
  const [raiseModal, setRaiseModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [descLen, setDescLen] = useState(0);

  // Asset list for dropdown

  const [assetListLoading, setAssetListLoading] = useState(false);

  // Detail / update modal
  const [updateModal, setUpdateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  /* ── Fetch tickets ── */
  const getData = async () => {
    try {
      setLoading(true);
      const { code, ticketList } = await CALL_API(
        `assets/asset-ticket/all`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) setTickets(ticketList || []);
    } catch (err) {
      console.error("Failed to fetch tickets", err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Fetch assets for dropdown ── */
  // const getAssets = async () => {
  //   try {
  //     setAssetListLoading(true);
  //     const { code, assetList: list } = await CALL_API(
  //       `assets/getassets/all`,
  //       "get"
  //     );
  //     if (code === STATUS_CODE.SUCCESS)
  //       setAssetList(Array.isArray(list) ? list : []);
  //   } catch (err) {
  //     console.error("Failed to fetch assets", err);
  //   } finally {
  //     setAssetListLoading(false);
  //   }
  // };

  useEffect(() => {
    getData();
  }, []);

  /* ── Filters ── */
  const filtered = tickets.filter((t) => {
    if (assetFilter.length && !assetFilter.includes(t.assetname)) return false;
    if (priorityFilter.length && !priorityFilter.includes(t.priority))
      return false;
    if (statusFilter.length && !statusFilter.includes(t.ticket_status))
      return false;
    return true;
  });

  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);

  const CSV_HEADERS = [
    { label: "Asset", key: "assetname" },
    { label: "Title", key: "title" },
    { label: "Priority", key: "priority" },
    { label: "Attachment", key: "ticket_filename" },
    { label: "Status", key: "ticket_status" },
    { label: "Applied On", key: "applied_on" },
    { label: "Description", key: "ticket_description" },
  ];

  // Register CSV export
  useEffect(() => {
    if (exportRef) {
      exportRef.current = () => {
        if (!filtered.length) {
          message.error("No data available to download.");
          return;
        }
        setCsvData(
          filtered.map((t) => ({
            assetname: t.assetname || "",
            title: t.title || "",
            priority: t.priority || "",
            ticket_filename: t.ticket_filename || "",
            ticket_status: t.ticket_status || "",
            applied_on: t.applied_on || "",
            ticket_description: t.ticket_description || "",
          }))
        );
        setTimeout(() => csvLinkRef.current?.link?.click());
      };
    }
  });

  /* ── Row click → open detail/update modal ── */
  const handleRowClick = (record) => {
    setSelectedTicket(record);
    setUpdateModal(true);
  };

  /* ── Update button click ── */
  const handleUpdateClick = (e, record) => {
    e.stopPropagation();
    setSelectedTicket(record);
    setUpdateModal(true);
  };

  /* ── Asset selection auto-fills Owner ── */
  const handleAssetChange = (assetid) => {
    const asset = assetList.find((a) => a.assetid === assetid);
    form.setFieldsValue({ owner: asset?.assignedto || undefined });
  };

  /* ── Open raise modal ── */
  const openRaiseModal = () => {
    setRaiseModal(true);
  };

  /* ── Close / reset raise modal ── */
  const closeRaiseModal = () => {
    setRaiseModal(false);
    form.resetFields();
    setFileList([]);
    setDescLen(0);
  };

  /* ── Raise ticket submit ── */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log("values", values);
      setSubmitLoading(true);
      const selectedAsset = assetList.find(
        (a) => a.assetid === values.assetname
      );

      const payload = {
        assetsname: selectedAsset?.assetname,
        owner: values.owner || "",
        issue_description: values.issue_description || "",
        service_provider: values.service_provider || "",
        additional_notes: values.additional_notes || "",
        service_send_by: userInfo?.userid,
        service_start_date: new Date(),
        assetid: values.assetname,
      };

      const { code } = await CALL_API(
        `assets/send-service-request`,
        "post",
        payload
      );
      if (code === STATUS_CODE.SUCCESS) {
        message.success("Ticket raised successfully");
        closeRaiseModal();
        getData();
      } else {
        throw new Error(`Failed: ${code}`);
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error("Failed to raise ticket");
      console.error(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePreviewClick = async (data) => {
    try {
      const preview = data?.ticket_filename?.split(".")?.pop();
      setLoading(true);

      const { code, url } = await CALL_API(
        `assets/ticket-file/${data?.ticketid}`,
        "get",
        {}
      );

      if (code === STATUS_CODE.SUCCESS) {
        if (["jpg", "jpeg", "png", "svg", "pdf"].includes(preview)) {
          setPreviewURL(url);
          setPreview(true);
        } else {
          download(url);
          setPreview(false);
        }
      } else {
        message.error("Preview failed. Try again.");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ── Table columns ── */
  const columns = [
    {
      title: "Asset",
      key: "asset",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.assetname}</div>
          <div style={{ color: "#888", fontSize: 12, fontStyle: "italic" }}>
            {Array.isArray(r.model) ? r.model[0] : r.model || ""}
          </div>
        </div>
      ),
    },
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Priority", dataIndex: "priority", key: "priority" },
    {
      title: "Attachment",
      dataIndex: "ticket_filename",
      key: "ticket_filename",
      render: (text, record) =>
        text ? (
          <a
            href="#"
            style={{ color: "#2d53da" }}
            className="text-wrap"
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewClick(record);
            }}
          >
            {text}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "ticket_status",
      key: "ticket_status",
      render: (v) => <StatusTag $status={v}>{v}</StatusTag>,
    },
    {
      title: "",
      key: "actions",
      width: 110,
      render: (_, record) =>
        (record?.ticket_status === "Open" ||
          record?.ticket_status === "In progress") &&
        Menulist?.find(
          (item) =>
            item?.key === "HRResources" &&
            item?.subMenu?.find(
              (subItem) =>
                subItem?.key === "assets" && subItem?.permission?.edit
            )
        ) ? (
          <Button
            type="primary"
            size="small"
            style={{ background: "#2563eb", borderRadius: 6, minWidth: 80 }}
            onClick={(e) => handleUpdateClick(e, record)}
          >
            Update
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      {/* Hidden CSV link */}
      <CSVLink
        headers={CSV_HEADERS}
        data={csvData}
        filename="tickets_and_queries.csv"
        ref={csvLinkRef}
        style={{ display: "none" }}
      />
      {/* ── Toolbar ── */}
      <Toolbar>
        <span style={{ fontWeight: 500, alignSelf: "center" }}>
          Total Tickets: {tickets.length}
        </span>

        <FilterGroup>
          <Form form={filterForm} layout="inline" style={{ marginBottom: 0 }}>
            <Form.Item
              name="assetFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="assetFilter"
                label="Asset"
                form={filterForm}
                options={[
                  ...new Set(tickets.map((t) => t.assetname).filter(Boolean)),
                ].map((name) => ({ label: name, value: name }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
            <Form.Item
              name="priorityFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="priorityFilter"
                label="Priority"
                form={filterForm}
                options={priorities.map((p) => ({ label: p, value: p }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
            <Form.Item
              name="statusFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="statusFilter"
                label="Status"
                form={filterForm}
                options={statuses.map((s) => ({ label: s, value: s }))}
                handleFormChange={() => {}}
              />
            </Form.Item>
          </Form>
          {Menulist?.find(
            (item) =>
              item?.key === "HRResources" &&
              item?.subMenu?.find(
                (subItem) =>
                  subItem?.key === "assets" && subItem?.permission?.edit
              )
          ) && (
            <Button
              type="primary"
              style={{ background: "#2563eb" }}
              onClick={openRaiseModal}
            >
              Send to Service
            </Button>
          )}
        </FilterGroup>
      </Toolbar>

      {/* ── Table ── */}

      <div style={{ overflowX: "auto" }}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filtered}
            pagination={false}
            size="middle"
            rowKey="ticketid"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: "pointer" },
            })}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              minWidth: 600,
            }}
          />
        </Spin>
      </div>

      {/* ── Ticket Detail / Update Modal ── */}
      <TicketUpdateModal
        open={updateModal}
        onClose={() => {
          setUpdateModal(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
        onSuccess={() => {
          setUpdateModal(false);
          setSelectedTicket(null);
          getData();
        }}
      />

      {/* ── Send Asset to Service Modal ── */}
      <Modal
        title="Send Asset to Service"
        open={raiseModal}
        onCancel={closeRaiseModal}
        centered
        width={500}
        footer={[
          <Button key="cancel" onClick={closeRaiseModal}>
            Cancel
          </Button>,

          <Button
            key="submit"
            type="primary"
            loading={submitLoading}
            style={{ background: "#2563eb" }}
            onClick={handleSubmit}
          >
            Sent to Service
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {/* Choose Asset */}
          <Form.Item
            label="Choose Asset"
            name="assetname"
            rules={[{ required: true, message: "Please select an asset" }]}
          >
            <Select
              placeholder="Select asset"
              style={{ width: "100%" }}
              loading={assetListLoading}
              showSearch
              optionFilterProp="children"
              onChange={handleAssetChange}
            >
              {assetList.map((a) => (
                <Option key={a.assetid} value={a.assetid}>
                  {a.assetname}
                  {a.model
                    ? ` – ${Array.isArray(a.model) ? a.model[0] : a.model}`
                    : ""}
                  {a.assignedto ? ` (${a.assignedto})` : ""}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Owner (Optional) — auto-filled from asset selection */}
          <Form.Item
            label={
              <OwnerLabel>
                Owner&nbsp;<span className="optional">(Optional)</span>
                <Tooltip
                  color="#1D4174"
                  title="Choosing an owner is useful when the issue is tied to their usage—like misuse, mishandling, or responsibility concerns. If the asset is still unassigned or you don’t suspect ownership-related issues, you can leave this field blank."
                >
                  <InfoCircleFilled
                    style={{ color: "#2563eb", fontSize: 14 }}
                  />
                </Tooltip>
              </OwnerLabel>
            }
            name="owner"
          >
            <Select
              placeholder="Select owner"
              style={{ width: "100%" }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {userList?.map((emp) => (
                <Option key={emp} value={emp?.userid}>
                  {emp?.fullname}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Issue Description */}
          <Form.Item
            label="Issue Description"
            name="issue_description"
            rules={[
              { required: true, message: "Please describe the issue" },
              { max: 250, message: "Max 250 characters" },
            ]}
          >
            <TextArea
              rows={4}
              maxLength={250}
              placeholder="Describe the issue..."
              style={{ borderRadius: 6 }}
              onChange={(e) => setDescLen(e.target.value.length)}
            />
          </Form.Item>
          {/* <CharCount>Maximum 250 characters ({descLen}/250)</CharCount> */}

          {/* Service Provider */}
          <Form.Item
            label="Service Provider"
            name="service_provider"
            rules={[
              { required: true, message: "Service provider is required" },
            ]}
            style={{ marginTop: 8 }}
          >
            <Input
              placeholder="e.g. QuickFix Solutions"
              style={{ borderRadius: 6 }}
            />
          </Form.Item>

          {/* Additional Notes */}
          <Form.Item
            label={
              <span>
                Additional Notes&nbsp;
                <span style={{ color: "#aaa", fontWeight: 400 }}>
                  (Optional)
                </span>
              </span>
            }
            name="additional_notes"
          >
            <TextArea
              rows={3}
              placeholder="Any extra details..."
              style={{ borderRadius: 6 }}
            />
          </Form.Item>
        </Form>
      </Modal>
      <ViewDocument
        open={preview}
        onCancel={() => {
          setPreview(false);
          setPreviewURL("");
        }}
        data={previewURL}
      />
    </div>
  );
};

export default TicketsAndQueries;
